/**
 * @description Service responsible for configuring and styling dialog backdrops. This service
 * is used both in dashboard dialogs and published apps dialogs.
 */
import { OverlayContainer } from '@angular/cdk/overlay';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { DialogSettingsInfo } from '@blaze-shared/api-contracts-domain';
import { generalSettingsSelectors } from '@blaze-shared/app-state-data-access';

@Injectable({ providedIn: 'root' })
export class DialogBackdropService {
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly store = inject(Store);

  readonly dialogSettings = signal<DialogSettingsInfo | null>(null);
  backdropColor: Signal<string>;
  backdropOpacity: Signal<string>;
  backgroundBlurAmount: Signal<string>;

  constructor() {
    this.initDialogStyleSignals();
    this.store.select(generalSettingsSelectors.selectDialogSettings).subscribe((dialogSettings) => {
      this.dialogSettings.set(dialogSettings ?? null);
    });
  }

  /**
   * Styles dialog backdrop with configured dialog appearance settings.
   * This has to be used in conjunction with opening a dialog with backdrop class being set
   * to 'bd-overlay-appearance', eg.:
   * this.dialog.open(MyDialogComponent, { backdropClass: 'bd-overlay-appearance' });
   */
  attachBackdropStyles(): void {
    const containerEl = this.overlayContainer.getContainerElement();
    const backdropEl = containerEl.querySelector('.bd-overlay-appearance') as HTMLElement | null;
    if (backdropEl) {
      backdropEl.style.setProperty('--dialog-backdrop-color', this.backdropColor());
      backdropEl.style.setProperty('--dialog-backdrop-opacity', this.backdropOpacity());
      backdropEl.style.setProperty('--dialog-background-blur-amount', this.backgroundBlurAmount());
    }
  }

  private initDialogStyleSignals(): void {
    this.backdropColor = computed(() => {
      const ds = this.dialogSettings();
      if (!ds?.hasBackdropColor || !ds?.backdropColor) {
        return '#000';
      }
      return ds.backdropColor;
    });

    this.backdropOpacity = computed(() => {
      const ds = this.dialogSettings();
      if (!ds?.hasBackdropColor || !ds?.backdropOpacity) {
        return '32%';
      }
      return `${ds.backdropOpacity.toString()}%`;
    });

    this.backgroundBlurAmount = computed(() => {
      const ds = this.dialogSettings();
      if (!ds?.hasBackgroundBlur) {
        return '0px';
      }
      return `${ds.backgroundBlurAmount.toString()}px`;
    });
  }
}
